/**
 * SessionConfig component - Configure session details before starting.
 *
 * Design: DENiZEN — matches Figma node 1:238 ("Session Setup")
 * - Small DENiZEN logo centred at top
 * - "Session Setup" heading (SN Pro Bold)
 * - Separator, then Session Type display + Location input
 * - Back + Continue buttons
 * - WCAG 2.1 AA compliant
 */

import { useState, useEffect, type FormEvent } from 'react'

interface ValidationErrors {
  location?: string
  participantCount?: string
  postcode?: string
}

const UK_POSTCODE_PATTERN = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/

export const AGE_RANGES = ['0-4', '5-9', '10-15', '16-17', '18-24', '25-34', '35-44', '45-54', '55-64', '65-74', '75-84', '85+'] as const // eslint-disable-line react-refresh/only-export-components
export type AgeRange = typeof AGE_RANGES[number]

export interface SessionConfigData {
  sessionType: 'individual' | 'group' | 'ambient'
  participantCount: number
  location: string
  facilitatorId: string
  facilitatorName?: string
  postcode?: string
  ageRanges?: AgeRange[]
}

export interface SessionConfigProps {
  /**
   * Session type selected in SessionStart
   */
  sessionType: 'individual' | 'group' | 'ambient'
  /**
   * Facilitator ID (pre-filled)
   */
  facilitatorId: string
  /**
   * Callback when configuration is confirmed
   */
  onConfirm: (config: SessionConfigData) => void
  /**
   * Callback to go back to session start
   */
  onBack: () => void
  /**
   * Whether the component is in a loading state
   */
  isLoading?: boolean
  /**
   * Error message to display
   */
  error?: string | null
}

export function SessionConfig({
  sessionType,
  facilitatorId,
  onConfirm,
  onBack,
  isLoading = false,
  error,
}: SessionConfigProps) {
  const [participantCount, setParticipantCount] = useState(sessionType === 'group' ? 2 : 1)
  const [facilitatorName, setFacilitatorName] = useState('')
  const [location, setLocation] = useState('')
  const [postcode, setPostcode] = useState('')
  const [ageRanges, setAgeRanges] = useState<AgeRange[]>([])
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})

  // Reset participant count when session type changes
  useEffect(() => {
    setParticipantCount(sessionType === 'group' ? 2 : 1)
  }, [sessionType])

  const validate = (): boolean => {
    const errors: ValidationErrors = {}

    if (!location.trim()) {
      errors.location = 'Please enter a location'
    }

    if (sessionType === 'group') {
      if (participantCount < 2 || participantCount > 4) {
        errors.participantCount = 'Group sessions require 2-4 participants'
      }
    }

    const trimmedPostcode = postcode.trim()
    if (trimmedPostcode && !UK_POSTCODE_PATTERN.test(trimmedPostcode)) {
      errors.postcode = "Enter a valid UK postcode (e.g., 'BA1 2QF') or leave blank"
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()

    if (validate() && !isLoading) {
      onConfirm({
        sessionType,
        participantCount: sessionType === 'individual' ? 1 : participantCount,
        location: location.trim(),
        facilitatorId,
        facilitatorName: facilitatorName.trim() || undefined,
        postcode: postcode.trim() || undefined,
        ageRanges: ageRanges.length > 0 ? ageRanges : undefined,
      })
    }
  }

  return (
    <div className="px-8 pb-6 sm:px-12 animate-slide-up">

      {/* Heading — per Figma node 1:238 */}
      <header className="mb-6 text-center">
        <h1 className="text-3xl font-display font-bold text-dark-green mb-2">
          Session Setup
        </h1>
        <p className="text-base font-body text-dark-green/70">
          {sessionType === 'individual'
            ? 'Configure your individual session'
            : 'Configure your group session'}
        </p>
      </header>

      <hr className="border-dark-green/15 mb-8" />

      <form onSubmit={handleSubmit} noValidate className="max-w-md mx-auto">

        {/* Session Type Display */}
        <div className="mb-6">
          <p className="text-xs font-display font-semibold text-dark-green/60 tracking-wider mb-3">
            Session Type
          </p>
          <div className="flex items-center gap-3 px-4 py-3 border border-dark-green/15 rounded-[10px]">
            <svg
              className="w-6 h-6 text-mid-green flex-shrink-0"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
            </svg>
            <span className="font-display font-semibold text-dark-green">
              {sessionType === 'individual' ? 'Solo Session' : 'Group Session'}
            </span>
          </div>
        </div>

        <hr className="border-dark-green/15 mb-6" />

        {/* Facilitator Name */}
        <div className="mb-6">
          <label
            htmlFor="facilitatorName"
            className="block text-xs font-display font-semibold text-dark-green tracking-wider mb-2"
          >
            Your Name
          </label>
          <input
            type="text"
            id="facilitatorName"
            value={facilitatorName}
            onChange={(e) => setFacilitatorName(e.target.value)}
            placeholder="e.g., Nick"
            maxLength={100}
            className="w-full px-4 py-3 font-body text-dark-green bg-white border-2 border-dark-green/15 rounded-2xl placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-mid-green transition-all duration-150"
            aria-describedby="facilitatorName-hint"
          />
          <p id="facilitatorName-hint" className="mt-2 text-xs font-body text-muted">
            Shown in the admin dashboard to identify who ran each session
          </p>
        </div>

        {/* Participant Count (Group only) */}
        {sessionType === 'group' && (
          <div className="mb-6">
            <label
              htmlFor="participantCount"
              className="block text-xs font-display font-semibold text-dark-green tracking-wider mb-2"
            >
              Number of Participants
            </label>
            <select
              id="participantCount"
              value={participantCount}
              onChange={(e) => setParticipantCount(Number(e.target.value))}
              className={`
                w-full px-4 py-3 font-body text-dark-green bg-white
                border-2 rounded-2xl
                focus:outline-none focus:ring-2 focus:ring-mid-green
                transition-all duration-150
                ${validationErrors.participantCount ? 'border-error' : 'border-dark-green/15'}
              `}
              aria-invalid={!!validationErrors.participantCount || undefined}
              aria-describedby={
                validationErrors.participantCount ? 'participantCount-error' : undefined
              }
            >
              <option value={2}>2 participants</option>
              <option value={3}>3 participants</option>
              <option value={4}>4 participants</option>
            </select>
            {validationErrors.participantCount && (
              <div
                id="participantCount-error"
                className="mt-2 px-3 py-2 bg-error/10 border border-dark-green/10 rounded-2xl text-error text-sm font-body"
                role="alert"
              >
                {validationErrors.participantCount}
              </div>
            )}
          </div>
        )}

        {/* Location Input */}
        <div className="mb-6">
          <label
            htmlFor="location"
            className="block text-xs font-display font-semibold text-dark-green tracking-wider mb-2"
          >
            Location
          </label>
          <input
            type="text"
            id="location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="e.g., Central Library, Room 201"
            className={`
              w-full px-4 py-3 font-body text-dark-green bg-white
              border-2 rounded-2xl
              placeholder:text-muted
              focus:outline-none focus:ring-2 focus:ring-mid-green
              transition-all duration-150
              ${validationErrors.location ? 'border-error' : 'border-dark-green/15'}
            `}
            aria-invalid={!!validationErrors.location || undefined}
            aria-describedby={validationErrors.location ? 'location-error' : 'location-hint'}
          />
          {validationErrors.location ? (
            <div
              id="location-error"
              className="mt-2 px-3 py-2 bg-error/10 border border-dark-green/10 rounded-2xl text-error text-sm font-body"
              role="alert"
            >
              {validationErrors.location}
            </div>
          ) : (
            <p id="location-hint" className="mt-2 text-xs font-body text-muted">
              Enter the venue or room where this session is taking place
            </p>
          )}
        </div>

        {/* Demographics (optional) */}
        <fieldset className="mb-6">
          <legend className="block text-xs font-display font-semibold text-dark-green tracking-wider mb-2">
            Demographics <span className="text-muted font-normal">(optional)</span>
          </legend>

          {/* Postcode */}
          <div className="mb-4">
            <label
              htmlFor="postcode"
              className="block text-xs font-body text-dark-green/70 mb-1"
            >
              Postcode
            </label>
            <input
              type="text"
              id="postcode"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value.toUpperCase())}
              placeholder="e.g., BA1 2QF"
              maxLength={8}
              className={`w-full px-4 py-3 font-body text-dark-green bg-white border-2 rounded-2xl placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-mid-green transition-all duration-150 ${validationErrors.postcode ? 'border-error' : 'border-dark-green/15'}`}
              aria-invalid={!!validationErrors.postcode || undefined}
              aria-describedby={validationErrors.postcode ? 'postcode-error' : 'postcode-hint'}
            />
            {validationErrors.postcode ? (
              <div
                id="postcode-error"
                className="mt-2 px-3 py-2 bg-error/10 border border-dark-green/10 rounded-2xl text-error text-sm font-body"
                role="alert"
              >
                {validationErrors.postcode}
              </div>
            ) : (
              <p id="postcode-hint" className="mt-1 text-xs font-body text-muted">
                Used for geographic analysis only — not linked to individuals
              </p>
            )}
          </div>

          {/* Age Range */}
          <div>
            <p className="text-xs font-body text-dark-green/70 mb-2">
              {sessionType === 'group' ? 'Age ranges represented' : 'Age range'}
            </p>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Age range selection">
              {AGE_RANGES.map((range) => {
                const isSelected = ageRanges.includes(range)
                const toggle = () => {
                  if (sessionType === 'individual') {
                    setAgeRanges(isSelected ? [] : [range])
                  } else {
                    setAgeRanges(isSelected
                      ? ageRanges.filter(r => r !== range)
                      : [...ageRanges, range]
                    )
                  }
                }
                return (
                  <button
                    key={range}
                    type="button"
                    onClick={toggle}
                    className={`
                      px-3 py-2 text-sm font-body rounded-[6px] border transition-all duration-150
                      ${isSelected
                        ? 'bg-mid-green text-white border-mid-green'
                        : 'bg-white text-dark-green border-dark-green/15 hover:border-mid-green/40'
                      }
                    `}
                    aria-pressed={isSelected}
                  >
                    {range}
                  </button>
                )
              })}
            </div>
          </div>
        </fieldset>

        {/* Error Display */}
        {error && (
          <div
            className="mb-6 p-4 bg-error/10 border border-dark-green/10 rounded-2xl"
            role="alert"
            aria-live="polite"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white rounded-full border border-dark-green/15 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-error" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-dark-green text-sm font-body">{error}</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-4 mt-8">
          <button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            className="btn-denizen-secondary flex-1 py-3"
          >
            Back
          </button>

          <button
            type="submit"
            disabled={isLoading}
            className="btn-denizen-primary flex-1 py-3"
            aria-busy={isLoading || undefined}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <SessionConfigLoadingSpinner />
                Setting Up...
              </span>
            ) : (
              'Continue'
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

function SessionConfigLoadingSpinner() {
  return (
    <svg
      className="w-5 h-5 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}

export default SessionConfig
